import { tenantConfig } from '@/lib/tenant'
import { getAuthToken } from '@/lib/auth/store'
import type { components, paths } from './schema'

export type HealthResponse = components['schemas']['HealthResponse']
export type EventsListResponse = components['schemas']['EventsListResponse']
export type EventSummary = components['schemas']['EventSummary']
export type EventDetailResponse = components['schemas']['EventDetailResponse']
export type ConversionsResponse = components['schemas']['ConversionsResponse']
export type BrandsListResponse = components['schemas']['BrandsListResponse']
export type SettingsResponse = components['schemas']['SettingsResponse']
export type DevLoginRequest = components['schemas']['DevLoginRequest']
export type DevLoginResponse = components['schemas']['DevLoginResponse']
export type ChannelToggleResponse = components['schemas']['ChannelToggleResponse']
export type ChannelConfigUpdateResponse = {
  effective_at: string
  dry_run: boolean
}
export type BrandConfig = Record<string, unknown>

export interface FbPixelEntry {
  pixel_id: string
  label?: string | null
  access_token?: string | null
}

export interface FbPixelsSettings {
  graph_version: string
  pixels: FbPixelEntry[]
}

export interface PaS2sSettings {
  postback_url: string
  aid?: string
  tid?: string
}

export interface TsS2sSettings {
  postback_url: string
  api_key?: string
  goal_id?: string
}

export interface BrandChannelSettings {
  fb?: FbPixelsSettings | null
  pa?: PaS2sSettings | null
  ts?: TsS2sSettings | null
}

export interface SettingsResponseExtended extends Omit<SettingsResponse, 'channel_settings'> {
  channel_settings?: Record<string, BrandChannelSettings>
}

export interface UserSummary {
  id: string
  email: string
  roles: string[]
  created_at: string
  last_active_at: string | null
}

export interface UsersListResponse {
  users: UserSummary[]
}

export interface InviteUserResponse {
  invitation_id: string
  email: string
  role: 'admin' | 'viewer'
  status: string
}

export interface RoleUpdateResponse {
  user_id: string
  role: 'admin' | 'viewer'
}

export interface EventsParams {
  since?: string
  until?: string
  event_name?: string
  limit?: number
  cursor?: string
}

export interface ConversionsParams {
  range?: '24h' | '7d' | '30d'
  tz?: string
}

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public body?: unknown,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

type QueryParams = Record<string, string | number | boolean | undefined | null>

function buildUrl(path: string, params?: QueryParams): string {
  const base = tenantConfig.apiBaseUrl.replace(/\/$/, '')
  if (!base) {
    throw new ApiError(
      'API base URL is not configured for this host. Check VITE_API_BASE_URL or tenant hostname defaults.',
      0,
    )
  }
  const url = new URL(`${base}${path}`)
  if (params) {
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== null && value !== '') {
        url.searchParams.set(key, String(value))
      }
    }
  }
  return url.toString()
}

async function request<T>(
  path: string,
  options: RequestInit & { params?: QueryParams; auth?: boolean } = {},
): Promise<T> {
  const { params, auth = true, ...init } = options
  const headers = new Headers(init.headers)

  if (init.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json')
  }

  if (auth) {
    const token = getAuthToken()
    if (token) {
      headers.set('Authorization', `Bearer ${token}`)
    }
  }

  const response = await fetch(buildUrl(path, params), {
    ...init,
    headers,
  })

  if (!response.ok) {
    let body: unknown
    try {
      body = await response.json()
    } catch {
      body = await response.text()
    }
    const detail =
      typeof body === 'object' && body !== null && 'detail' in body
        ? String((body as { detail: unknown }).detail)
        : response.statusText
    throw new ApiError(detail || `HTTP ${response.status}`, response.status, body)
  }

  if (response.status === 204) {
    return undefined as T
  }

  return response.json() as Promise<T>
}

export const api = {
  getHealthz(): Promise<Record<string, string>> {
    return request('/healthz', { auth: false })
  },

  getHealth(): Promise<HealthResponse> {
    return request('/v1/health')
  },

  getEvents(params: EventsParams = {}): Promise<EventsListResponse> {
    return request('/v1/events', { params: params as QueryParams })
  },

  getEvent(eventId: string): Promise<EventDetailResponse> {
    return request(`/v1/events/${encodeURIComponent(eventId)}`)
  },

  getConversions(params: ConversionsParams = {}): Promise<ConversionsResponse> {
    return request('/v1/conversions', { params: params as QueryParams })
  },

  getBrands(): Promise<BrandsListResponse> {
    return request('/v1/brands')
  },

  getBrandConfig(brandId: string): Promise<BrandConfig> {
    return request(`/v1/brands/${encodeURIComponent(brandId)}/config`)
  },

  patchChannel(
    brandId: string,
    channel: string,
    enabled: boolean,
    confirmLive = false,
  ): Promise<ChannelToggleResponse> {
    const headers: Record<string, string> = {}
    if (confirmLive) {
      headers['X-Confirm-Live-Dispatch'] = 'true'
    }
    return request(
      `/v1/brands/${encodeURIComponent(brandId)}/channels/${encodeURIComponent(channel)}`,
      {
        method: 'PATCH',
        body: JSON.stringify({ enabled }),
        headers,
      },
    )
  },

  getSettings(): Promise<SettingsResponseExtended> {
    return request('/v1/settings')
  },

  patchFbPixels(
    brandId: string,
    body: { graph_version?: string; pixels: FbPixelEntry[] },
  ): Promise<ChannelConfigUpdateResponse> {
    return request(`/v1/brands/${encodeURIComponent(brandId)}/channels/fb/pixels`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    })
  },

  patchPaS2s(
    brandId: string,
    body: { postback_url: string; aid?: string; tid?: string },
  ): Promise<ChannelConfigUpdateResponse> {
    return request(`/v1/brands/${encodeURIComponent(brandId)}/channels/pa/s2s`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    })
  },

  patchTsS2s(
    brandId: string,
    body: { postback_url: string; api_key?: string; goal_id?: string },
  ): Promise<ChannelConfigUpdateResponse> {
    return request(`/v1/brands/${encodeURIComponent(brandId)}/channels/ts/s2s`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    })
  },

  devLogin(body: DevLoginRequest): Promise<DevLoginResponse> {
    return request('/v1/dev/login', {
      method: 'POST',
      body: JSON.stringify(body),
      auth: false,
    })
  },

  listUsers(params?: { limit?: number; offset?: number }): Promise<UsersListResponse> {
    return request('/v1/users', { params: params as QueryParams })
  },

  inviteUser(email: string, role: 'admin' | 'viewer'): Promise<InviteUserResponse> {
    return request('/v1/users/invite', {
      method: 'POST',
      body: JSON.stringify({ email, role }),
    })
  },

  setUserRole(userId: string, role: 'admin' | 'viewer'): Promise<RoleUpdateResponse> {
    return request(`/v1/users/${encodeURIComponent(userId)}/role`, {
      method: 'PATCH',
      body: JSON.stringify({ role }),
    })
  },
}

export type ApiClient = typeof api

// Re-export path types for advanced usage
export type { paths, components }
